import dayjs, {type Dayjs} from 'dayjs';
import {useUserStore} from '../stores/userStore';
import {useAccountStore} from '../stores/accountStore';
import {useSecurityStore} from '../stores/securityStore';
import {useGlobalStore} from '../stores/globalStore';
import {logUuid, reportError} from '@deskly/observability';
import {postMessage} from './helpers/messageHelper';

const requestMap = new Map<string, Dayjs[]>();

const requestThreshold = {
    seconds: 10,
    count: 20,
};

function getUserReportContext() {
    try {
        const {user} = useUserStore();
        const {account} = useAccountStore();
        const {authType} = useSecurityStore();

        return {
            userId: logUuid(user?.id),
            accountId: logUuid(account?.id),
            usesMsAccessToken: authType === 'MS-Access-Token',
            userContextFailed: false,
        };
    } catch {
        return {userId: null, accountId: null, usesMsAccessToken: null, userContextFailed: true};
    }
}

function clearOldEntries() {
    const deletionThresholdDate = dayjs().subtract(30, 'second');

    for (const key of requestMap.keys()) {
        const urlRequests = requestMap.get(key)!;
        const currentRequests = urlRequests.filter(timestamp =>
            timestamp.isAfter(deletionThresholdDate)
        );
        if (!currentRequests.length) {
            requestMap.delete(key);
        } else if (currentRequests.length !== urlRequests.length) {
            requestMap.set(key, currentRequests);
        }
    }
}

export const requestLoopDetector = {
    isActive: true,
    check(url: string): boolean {
        if (!this.isActive) {
            return true;
        }
        if (!requestMap.has(url)) {
            requestMap.set(url, []);
        }

        const urlRequests = requestMap.get(url)!;
        urlRequests.push(dayjs());
        clearOldEntries();

        const thresholdDate = dayjs().subtract(requestThreshold.seconds, 'second');
        const requestsAfterThreshold = urlRequests.filter(timestamp =>
            timestamp.isAfter(thresholdDate)
        );

        if (requestsAfterThreshold.length >= requestThreshold.count) {
            const userReportContext = getUserReportContext();
            let resetWithExceptionFailed = false;

            try {
                useGlobalStore().resetWithExceptions();
            } catch {
                resetWithExceptionFailed = true;
                // resetWithExceptions failed — directly reset security store so that
                // the MSAL flag is cleared before reload. Without this, the app
                // restarts with msal=true in secure storage but no MSAL accounts in
                // localStorage, causing an immediate failed re-login attempt.
                try {
                    useSecurityStore().reset();
                } catch {
                    // best-effort; the reload below will enforce a clean state
                }
            }

            // The URLs stay out: their paths and query strings carry search terms and names.
            reportError(new Error('checkRequestLoop'), {
                ...userReportContext,
                loopingRequestCount: requestsAfterThreshold.length,
                trackedUrlCount: requestMap.size,
                resetWithExceptionFailed,
            });

            localStorage.clear();
            postMessage('desklyTokenExpired');
            window.location.reload();

            return false;
        }

        return true;
    },
};
